/**
 * Allied Brass Product Card Web Component
 * Handles swatch hover preview, sticky click selection, and expandable swatch tray.
 * Uses event delegation on the component root to prevent memory leaks
 * from Section Rendering API re-renders.
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABProductCard extends ABBaseComponent {
  connectedCallback() {
    this.img = this.$('.ab-product-card__media img');
    if (!this.img) return;

    this.originalSrc = this.img.src;
    this.originalSrcset = this.img.srcset || '';

    // Track the currently selected variant ID for bundle use
    this.selectedVariantId = null;
    this.selectedFinishName = null;

    // Find the initially selected swatch (if any) and record its variant
    const selectedDot = this.$('.ab-product-card__swatch-dot.is-selected');
    if (selectedDot) {
      this.selectedVariantId = selectedDot.dataset.variantId || null;
      this.selectedFinishName = selectedDot.dataset.finishName || null;
    }

    // Event delegation — listen on component root
    this._onClick = this._handleClick.bind(this);
    this._onMouseover = this._handleMouseover.bind(this);
    this._onMouseleave = this._handleMouseleave.bind(this);
    this._onFocusin = this._handleFocusin.bind(this);
    this._onFocusout = this._handleFocusout.bind(this);

    this.addEventListener('click', this._onClick);
    this.addEventListener('mouseover', this._onMouseover);
    this.addEventListener('focusin', this._onFocusin);
    this.addEventListener('mouseleave', this._onMouseleave);
    this.addEventListener('focusout', this._onFocusout);
  }

  // ─── Click: sticky swatch selection + expand toggle ───

  _handleClick(e) {
    // Expand/collapse swatch tray
    const expandBtn = e.target.closest('[data-expand-swatches]');
    if (expandBtn) {
      e.preventDefault();
      e.stopPropagation();
      const swatches = this.$('.ab-product-card__swatches');
      if (swatches) swatches.classList.toggle('is-expanded');
      return;
    }

    // Sticky swatch selection
    const dot = e.target.closest('.ab-product-card__swatch-dot');
    if (dot) {
      e.preventDefault();
      e.stopPropagation();
      this._selectSwatch(dot);
      return;
    }
  }

  /**
   * Permanently select a swatch: update image, track variant, show gold ring.
   */
  _selectSwatch(dot) {
    const url = dot.dataset.variantImage;

    // Update image if variant image is available
    if (url && this.img) {
      this.img.src = url;
      this.img.srcset = '';
      this.originalSrc = url;
      this.originalSrcset = '';
    }

    // Always track selected variant for bundle use (even without image)
    this.selectedVariantId = dot.dataset.variantId || null;
    this.selectedFinishName = dot.dataset.finishName || null;

    // Update selected visual state (gold ring)
    // Clear both is-selected (JS) and is-active (server Liquid) gold rings
    this.$$('.ab-product-card__swatch-dot').forEach(d => {
      d.classList.remove('is-selected');
      d.setAttribute('aria-label', d.dataset.finishName || '');
    });
    this.$$('.ab-finish-swatch.is-active').forEach(s => s.classList.remove('is-active'));
    dot.classList.add('is-selected');
    dot.setAttribute('aria-label', (dot.dataset.finishName || '') + ' (selected)');

    // Dispatch event for bundle builder or other listeners
    this.dispatchEvent(new CustomEvent('swatch:selected', {
      bubbles: true,
      detail: {
        productHandle: this.dataset.productHandle,
        variantId: this.selectedVariantId,
        finishName: this.selectedFinishName,
        imageUrl: url,
      }
    }));
  }

  // ─── Hover: temporary image preview ───

  _swapImage(dot) {
    const url = dot.dataset.variantImage;
    if (url && this.img) {
      this.img.src = url;
      this.img.srcset = '';
    }
  }

  _restoreImage() {
    if (this.img) {
      this.img.src = this.originalSrc;
      this.img.srcset = this.originalSrcset;
    }
  }

  _handleMouseover(e) {
    const dot = e.target.closest('.ab-product-card__swatch-dot');
    if (dot) this._swapImage(dot);
  }

  _handleFocusin(e) {
    const dot = e.target.closest('.ab-product-card__swatch-dot');
    if (dot) this._swapImage(dot);
  }

  _handleMouseleave(e) {
    if (!this.contains(e.relatedTarget)) {
      this._restoreImage();
    }
  }

  _handleFocusout(e) {
    if (!this.contains(e.relatedTarget)) {
      this._restoreImage();
    }
  }

  // ─── Cleanup ───

  disconnectedCallback() {
    if (this._onClick) {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('mouseover', this._onMouseover);
      this.removeEventListener('focusin', this._onFocusin);
      this.removeEventListener('mouseleave', this._onMouseleave);
      this.removeEventListener('focusout', this._onFocusout);
    }
    this.img = null;
    this.originalSrc = null;
    this.originalSrcset = null;
    this.selectedVariantId = null;
    this.selectedFinishName = null;
  }
}

defineComponent('ab-product-card', ABProductCard);
